import DateFormat from '@/Enums/Format/DateFormat.enum';
import GeoType from '@/Enums/Geography/GeographyType.enum';
import { IAudienceMeta } from '@/Interfaces/Audiences/Audience.interface';
import type { IForecast } from '@/Interfaces/Forecaster/Forecast.interface';
import { ITargetingLocation } from '@/Interfaces/Location.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import GeographyChips from '@/components/Grid/GeographyChips';
import { EstimationTabLabels } from '@/constants/Forecaster/EstimationTabs';
import { formatAudienceExpression } from '@/helpers/audience/formatAudienceExpression';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import ActionsCell from '@/pages/ForecasterTable/DataGrid/ActionsCell';
import AudienceCell from '@/pages/ForecasterTable/DataGrid/AudienceCell';
import { Share as ShareIcon } from '@mui/icons-material';
import { Link, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import moment from 'moment';
import ViewLength from './ViewLength';

const convertGeoArrayToString = (value: ITargetingLocation[]): string => {
  const geoObject: Record<GeoType, string[]> = value.reduce(
    (acc: Record<GeoType, string[]>, value) => {
      if (!acc[value.type]) {
        acc[value.type] = [value.displayName];
      } else {
        acc[value.type].push(value.displayName);
      }
      return acc;
    },
    {} as Record<GeoType, string[]>,
  );

  return Object.entries(geoObject)
    .map(([key, arr]) => `${key}: ${arr.join(', ')}`)
    .join('; ');
};

const audienceValueFormatter = (value: IAudienceMeta, row: IForecast) => {
  if (row.audienceBlocksMeta?.length) {
    return formatAudienceExpression(row.audienceBlocksMeta);
  }

  if (!AudienceTargetingService.checkAudiencesInTargeting(value)) {
    return '';
  }

  const includeAndGroups = (value.include ?? [])
    .map(orGroup => `(${orGroup.map(value => value.name).join(' OR ')})`)
    .join(' AND ');
  const exlcudeGroups = (value.exclude ?? []).map(entry => entry.name).join(' OR ');

  return [includeAndGroups, exlcudeGroups].filter(Boolean).join(' EXCLUDE ');
};
const geographicValueFormatter = (value: IForecast['geographic']) => {
  if (!value) {
    return '';
  }

  if (!Array.isArray(value)) {
    return 'United States';
  }

  return convertGeoArrayToString(value);
};

const geographicValueGetter = (_value: unknown, row: IForecast) => {
  const value = row.geographic;

  const fence = row.geo_fence;

  if (fence) {
    return '';
  }

  if (!value || !value.length) {
    return 'United states';
  }

  return value;
};
const geoFenceValueFormatter = (value: IForecast['geo_fence']) => {
  if (!value) {
    return '';
  }

  return `${value.address}, ${value.radius} miles`;
};
const geoExclusionsValueFormatter = (value: IForecast['geo_exclusion']) => {
  if (!value) {
    return '';
  }

  return convertGeoArrayToString(value);
};

const getColumns = (
  loadForecast: (id: IForecast['id'], currentChannel: IForecast['name']) => void,
  onShareForecasterItem: (id: IForecast['id']) => void,
  onDeleteForecasterItem: (row: IForecast) => void,
): GridColDef[] => [
  {
    field: 'forecast_name',
    headerName: 'Forecast Name',
    flex: 1.3,
    display: 'flex',
    renderCell: ({ value, row }: GridRenderCellParams<IForecast>) => (
      <Link
        component='button'
        underline='hover'
        sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
        onClick={() => loadForecast(row.id, row.name)}
      >
        {value}{' '}
        {row.shares ? (
          <Tooltip title={row.shares?.shareUser ? `Shared by ${row.shares.shareUser?.email}` : 'Shared'}>
            <ShareIcon fontSize='small' color='primary' />
          </Tooltip>
        ) : null}
      </Link>
    ),
  },
  {
    field: 'audienceMeta',
    flex: 1.5,
    minWidth: 130,
    headerName: 'Audience',
    valueFormatter: audienceValueFormatter,
    display: 'flex',
    renderCell: AudienceCell,
  },
  {
    field: 'geographic',
    width: 215,
    flex: 1.25,
    headerName: 'Geographic',
    valueFormatter: geographicValueFormatter,
    valueGetter: geographicValueGetter,
    display: 'flex',
    renderCell: GeographyChips,
    sortable: false,
  },
  {
    field: 'geo_fence',
    headerName: 'Geo Fence',
    valueFormatter: geoFenceValueFormatter,
    sortable: false,
  },
  {
    field: 'geo_exclusion',
    headerName: 'Geo Exclusion',
    valueFormatter: geoExclusionsValueFormatter,
    sortable: false,
  },
  {
    field: 'productName',
    headerName: 'Package',
    align: 'center',
    headerAlign: 'center',
    width: 80,
    valueFormatter: (value: IForecast['productName']) => (value ? value : 'RON'),
  },
  {
    field: 'tab_index',
    headerName: 'Length',
    flex: 1.25,
    align: 'center',
    headerAlign: 'center',
    minWidth: 130,
    valueFormatter: (value: IForecast['tab_index']) => EstimationTabLabels[value],
    display: 'flex',
    renderCell: ({ value, row }: GridRenderCellParams<IForecast>) => (
      <ViewLength lengthId={value} value={row.date_range} />
    ),
  },
  {
    field: 'impressions',
    headerName: 'Impressions Across All Channels',
    align: 'center',
    headerAlign: 'center',
    width: 125,
    valueFormatter: (value: IForecast['impressions']) => (value ? numberToUsFormat(value) : 0),
  },
  {
    field: 'created_at',
    headerName: 'Timestamp',
    align: 'center',
    headerAlign: 'center',
    width: 110,
    valueFormatter: (value: IForecast['created_at']) => value,
    display: 'flex',
    renderCell: ({ value }: GridRenderCellParams<IForecast>) =>
      value ? (
        <div>
          <div>{moment(value).format(DateFormat.DayShortMonthYear)}</div>
          <div className='text-[#84818A]'>{moment(value).format(DateFormat.LongTime)}</div>
        </div>
      ) : null,
  },
  {
    field: 'action',
    headerName: 'Action',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    display: 'flex',
    renderCell: ({ row }: GridRenderCellParams<IForecast>) => (
      <ActionsCell onDelete={() => onDeleteForecasterItem(row)} onShare={() => onShareForecasterItem(row.id)} />
    ),
    maxWidth: 245,
    disableExport: true,
  },
];

export default getColumns;
